import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import isEqual from "lodash/isEqual";

import {
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  TextareaAutosize,
  Typography,
  Grid,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from "@mui/material";

import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import CustomDropdown from "../../../reusable-components/CustomDropdown/CustomDropdown";
import AddEditButton from "../../../reusable-components/AddEditButton/AddEditButton";
import CustomDatePicker from "../../../containers/CustomDatePicker/CustomDatePicker";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import DialogTitle from "../../../containers/DialogTitle/DialogTitle";
import DialogActions from "../../../containers/DialogActions/DialogActions";
import ConfirmationChangesDialog from "../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";

import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import { convertDateToUTC, formatDateToMUIFormat } from "../../../utils/time-utils";

import { validateOnAddItem, validateOnEditItem } from "./utils";

import { Props, State, DisablingFields, FormField } from "./interfaces";
import { Content } from "./styles";
import { DTOModel } from "../../../models/base/dto-model";
import { STOModel } from "../../../models/base/sto-model";
import { TextFieldTypeAmount } from "./TextFieldTypeAmount/TextFieldTypeAmount";
import {
  EMAIL_PATTERN,
  isValidString,
  listOfDisallowedCharsInEmailAddress,
} from "../../../utils/validation/isValidString";
import EmailIsNotValid from "../../EmailIsNotValid/EmailIsNotValid";

const AddEditItem = ({
  titleForEditComponent,
  effect = "add",
  item,
  children,
  handleCancel,
  handleReload,
  handleResetDefaultItem,
  serviceApiInstance,
  updateGlobalState,
  formFields,
  buttonVariant,
  verticalOrientation = false,
  isMutated,
  handleNotifyParent,
  disabled,
  customAddEditButtonStyle,
  buttonText,
  handleSavedItem,
}: Props) => {
  const { t } = useTranslation();

  const [state, setState] = useState<State>({
    item: null,
    resetItem: null,
    loading: false,
    error: "",
    isDialogOpen: false,
    isConfirmationChangesDialogOpen: false,
  });

  const [disablingFields, setDisablingFields] = useState<DisablingFields>({
    fields: formFields?.filter((formField) => formField.fieldType === "disablingCheckbox").length
      ? formFields
          ?.filter((formField) => formField.fieldType === "disablingCheckbox")
          .map((formField) => ({
            field: formField.field,
            value:
              effect === "add"
                ? formField.defaultValue === "true"
                  ? true
                  : false
                : !!item && formField.dependentField
                ? item[formField.dependentField as keyof Partial<DTOModel>]
                  ? false
                  : true
                : false,
          }))
      : [],
  });

  const fieldsWithTypeEmail = useMemo(() => {
    return formFields?.filter((item) => item.type === "email")?.map((item) => item.field) ?? [];
  }, [formFields]);

  const [fieldsWithTypeEmailNotValidated, setFieldsWithTypeEmailNotValidated] = useState<string[]>([]);

  const checkEmailsForValidation = useCallback(
    (value: string, field: string) => {
      if (!value) {
        setFieldsWithTypeEmailNotValidated(fieldsWithTypeEmailNotValidated.filter((currField) => currField !== field));
      } else {
        const emailIsValid = isValidString(EMAIL_PATTERN, value);

        emailIsValid
          ? setFieldsWithTypeEmailNotValidated(
              fieldsWithTypeEmailNotValidated.filter((currField) => currField !== field)
            )
          : setFieldsWithTypeEmailNotValidated([...fieldsWithTypeEmailNotValidated, field]);
      }
    },
    [fieldsWithTypeEmailNotValidated]
  );

  useEffect(() => {
    if (!!fieldsWithTypeEmail.length) {
      fieldsWithTypeEmail.forEach((fieldWithEmail) => {
        checkEmailsForValidation((item as any)?.[fieldWithEmail] ?? "", fieldWithEmail);
      });
    }
  }, [fieldsWithTypeEmail]);

  const [fieldsWithControlledProps, setFieldsWithControlledProps] = useState<FormField[]>([]);

  const handleDisablingfieldChange = useCallback(
    (field: string, value: boolean) => {
      const dependentFieldName = formFields?.filter((formField) => formField.field === field)[0].dependentField;
      const dependentField: FormField | undefined = formFields?.filter(
        (formField) => formField.field === dependentFieldName
      )[0];
      const dependentFieldDefaultValue =
        dependentField?.type === "date"
          ? dependentField?.defaultValue && Date.parse(dependentField?.defaultValue)
          : dependentField?.defaultValue ?? null;

      setDisablingFields((state) => ({
        ...state,
        fields: [
          ...disablingFields?.fields?.filter((_field) => _field.field !== field),
          {
            field: field,
            value: value,
          },
        ],
      }));

      //when disabling the dependentfield set it's value to null to prevent sending previous value to db

      if (value && !!dependentFieldName) {
        setState((state) => ({
          ...state,
          item: {
            ...state.item,
            [dependentFieldName]: null,
          },
        }));
      } else if (!value && !!dependentFieldName) {
        setState((state) => ({
          ...state,
          item: {
            ...state.item,
            [dependentFieldName]: dependentFieldDefaultValue,
          },
        }));
      }
    },
    [state.item]
  );

  useEffect(() => {
    setState((state) => ({ ...state, item, resetItem: item }));
  }, [item]);

  const setIsDialogOpen = (value: boolean) => setState((state) => ({ ...state, isDialogOpen: value }));

  const setIsConfirmationChangesDialogOpen = (value: boolean) =>
    setState((state) => ({ ...state, isConfirmationChangesDialogOpen: value }));

  const handleCloseDialog = () => {
    if (!isEqual(state.item, state.resetItem)) {
      setIsConfirmationChangesDialogOpen(true);
    } else setIsDialogOpen(false);
  };

  const handleDiscardChanges = () => {
    handleCancel && handleCancel(state.resetItem, effect);

    setState((state) => ({
      ...state,
      item,
      resetItem: item,
      isDialogOpen: false,
      isConfirmationChangesDialogOpen: false,
      error: "",
    }));
  };

  const handleSave = async (): Promise<void> => {
    setState((state) => ({ ...state, error: "", loading: true }));

    try {
      const id = uuidv4();

      const emailsToLowerCase: { [key: string]: string } = {};

      fieldsWithTypeEmail.forEach((emailField) => {
        if (!!state.item[emailField]) {
          emailsToLowerCase[emailField] = state.item[emailField].toLowerCase().trim();
        }
      });

      const datesToUTC: { [key: string]: Date } = {};

      const fieldsWithTypeDate =
        formFields?.filter((item) => item.fieldType === "datePicker")?.map((item) => item.field) ?? [];

      fieldsWithTypeDate.forEach((dateField) => {
        if (!!state.item[dateField]) {
          datesToUTC[dateField] =
            state.item[dateField] instanceof Date ? convertDateToUTC(state.item[dateField]) : state.item[dateField];
        }
      });

      const allTextfieldsValues: Record<string, string> = {};

      const fieldsWithTypeTextfield =
        formFields
          ?.filter(
            (item) =>
              (item.fieldType === "textfield" || !item.hasOwnProperty("fieldType")) && !item.hasOwnProperty("type")
          )
          ?.map((item) => item.field) ?? [];

      fieldsWithTypeTextfield.forEach((textfield) => {
        if (!!state.item[textfield]) {
          allTextfieldsValues[textfield] = state.item[textfield].trim();
        }
      });

      const toBeSavedItem: STOModel =
        effect === "add"
          ? ({
              ...state.item,
              ...emailsToLowerCase,
              ...datesToUTC,
              ...allTextfieldsValues,
              id: uuidv4(),
            } as STOModel)
          : ({ ...state.item, ...emailsToLowerCase, ...datesToUTC, ...allTextfieldsValues } as STOModel);

      if (serviceApiInstance) {
        const response = await serviceApiInstance.saveItems([toBeSavedItem]);

        toastifyResponse(response, "all", "default_success_message", "failure_saving_data", "error_saving_data");

        if (!response.error && effect === "edit") {
          if (isMutated) {
            isMutated.current = true;
          }
        }

        if (updateGlobalState) await updateGlobalState();
        handleNotifyParent && handleNotifyParent();

        if (!response.error) {
          handleSavedItem && handleSavedItem(toBeSavedItem);
        }
      }

      if (effect === "add" && handleResetDefaultItem) handleResetDefaultItem();

      setState((state) => ({
        ...state,
        item,
        resetItem: item,
        isDialogOpen: false,
        loading: false,
      }));

      handleReload && (await handleReload());
    } catch (error: any) {
      setState((state) => ({
        ...state,
        loading: false,
        error: error?.message ?? error,
      }));
    }
  };

  const handleChange = (field: string, value: any) => {
    setState((state) => ({
      ...state,
      item: { ...state.item, [field]: value },
    }));

    handleResetFieldsWithControlledProps(field);
  };

  const handleResetFieldsWithControlledProps = (field: string) => {
    const fieldWithControlledProp = fieldsWithControlledProps.filter(
      (formField) => formField.controlledPropFilter?.filteringField === field
    )[0];

    if (fieldWithControlledProp) {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          [fieldWithControlledProp.field]: fieldWithControlledProp.controlledPropFilter?.defaultFieldValue,
        },
      }));
    }
  };

  useEffect(() => {
    if (formFields) {
      const fieldsWithControlledPropsFiltered = formFields.filter((formField) => !!formField.controlledPropFilter);

      if (fieldsWithControlledPropsFiltered.length > 0) {
        setFieldsWithControlledProps([...fieldsWithControlledPropsFiltered]);
      }
    }
  }, [formFields]);

  return (
    <>
      <AddEditButton
        title={t(`buttons.${effect}`)}
        effect={effect}
        onClick={() => setIsDialogOpen(true)}
        disabled={disabled}
        customStyle={{ ...customAddEditButtonStyle }}
        buttonText={buttonText}
      >
        {children}
      </AddEditButton>
      <Dialog
        fullWidth
        open={state.isDialogOpen}
        onClose={handleCloseDialog}
      >
        <DialogTitle
          title={titleForEditComponent}
          handleClose={handleCloseDialog}
        />
        <Content verticalOrientation={verticalOrientation}>
          {formFields &&
            formFields.map(
              ({
                field,
                fieldProp,
                childField,
                childFieldProp,
                labelKey,
                labelForRadio,
                fieldType = "textfield",
                type,
                isMultiSelect,
                apiService,
                apiLoadItemsPath,
                unknownOptionLabel,
                defaultValueID,
                defaultValue,
                values,
                required = true,
                propsFilter,
                excludeIds,
                isReadOnly,
                selectedValue,
                disablingField,
                dependentField,
                endAdornment,
                placeHolder,
                disabled = false,
                controlledPropFilter,
                multiline,
                minRows,
                fullWidth,
              }) => {
                return (
                  <React.Fragment key={field}>
                    {type === "amount" ? (
                      <TextFieldTypeAmount
                        key={field}
                        placeHolder={placeHolder}
                        required={required}
                        field={field}
                        labelKey={labelKey}
                        amountValue={state.item?.[field]}
                        handleChange={handleChange}
                        disabled={disabled}
                      />
                    ) : type === "number" ? (
                      <TextField
                        required={required}
                        variant="outlined"
                        label={labelKey}
                        type={"number"}
                        inputProps={{
                          step: 0.1,
                          inputMode: "numeric",
                        }} // we could use pattern: type === "number" ? "[0-9]*" : null
                        value={state.item?.[field]}
                        onChange={({ target: { value } }) => handleChange(field, value)}
                        InputProps={{
                          // ...params.InputProps,
                          // disabled: disabled,
                          endAdornment: <>{!!endAdornment && endAdornment}</>,
                          // style: {
                          //   ...inputStyle,
                          // },
                        }}
                        onKeyDown={(e) => {
                          if (type === "number") e.key === "e" && e.preventDefault();
                        }}
                        disabled={disabled}
                      />
                    ) : fieldType === "textarea" ? (
                      <Grid style={{ width: "100%" }}>
                        <Typography style={{ opacity: 0.7 }}>{labelKey}</Typography>
                        <TextareaAutosize
                          required={required}
                          minRows={5}
                          style={{ width: "100%", borderRadius: "12px", padding: "10px" }}
                          key={field}
                          placeholder={labelKey}
                          value={state.item?.[field] ? state.item?.[field] : ""}
                          onChange={({ target: { value } }) => handleChange(field, value)}
                          disabled={disabled}
                        />
                      </Grid>
                    ) : fieldType === "textfield" ? (
                      <>
                        <TextField
                          sx={{ ...(!!fullWidth && { width: "100% !important" }) }}
                          placeholder={placeHolder}
                          required={required}
                          variant="outlined"
                          label={labelKey}
                          type={type ?? "text"}
                          value={
                            type === "date"
                              ? !!state.item?.[field] && formatDateToMUIFormat(state.item?.[field])
                              : state.item?.[field]
                          }
                          onChange={({ target: { value } }) => {
                            if (type === "date") {
                              handleChange(field, value);
                            } else if (type === "email") {
                              setFieldsWithTypeEmailNotValidated(
                                fieldsWithTypeEmailNotValidated.filter((currField) => currField !== field)
                              );

                              handleChange(field, value.trim());
                            } else {
                              handleChange(field, value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (type === "email") {
                              // Prevent the entry of disallowed characters in email address
                              if (listOfDisallowedCharsInEmailAddress.includes(e.key)) {
                                e.preventDefault();
                              }
                            }
                          }}
                          InputLabelProps={{
                            shrink: type === "date" || state.item?.[field] !== "" || !!placeHolder,
                          }}
                          disabled={
                            !!disablingField
                              ? disablingFields.fields.filter(
                                  (_disablingField) => _disablingField.field === disablingField
                                )[0].value
                              : disabled
                          }
                          onBlur={(e) => {
                            if (type === "email") {
                              checkEmailsForValidation(e.target.value, field);
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <>
                                {type === "email" && fieldsWithTypeEmailNotValidated.includes(field) && (
                                  <EmailIsNotValid />
                                )}
                              </>
                            ),
                          }}
                          error={fieldsWithTypeEmailNotValidated.includes(field)}
                          multiline={!!multiline}
                          {...(!!minRows && {
                            minRows: minRows,
                          })}
                        />
                      </>
                    ) : fieldType === "dropdown" ? (
                      <AutocompleteDropdown
                        required={required}
                        labelKey={labelKey}
                        isMultiSelect={isMultiSelect}
                        field={field as never} // TEMPORARY
                        fieldProp={fieldProp}
                        childField={childField} // TEMPORARY
                        childFieldProp={childFieldProp}
                        propsItem={state.item}
                        serviceApi={apiService}
                        apiLoadItemsPath={apiLoadItemsPath}
                        handleChange={(field, value) => handleChange(field, value)}
                        unknownOptionLabel={unknownOptionLabel}
                        propsFilter={{
                          ...(propsFilter ?? {}),
                          ...(state.item &&
                            controlledPropFilter &&
                            state.item[controlledPropFilter.filteringField] && {
                              [controlledPropFilter.propFilter]: controlledPropFilter.propFilterIsArray
                                ? [
                                    state.item[controlledPropFilter.filteringField][
                                      controlledPropFilter.filteringChildField
                                    ],
                                  ]
                                : state.item[controlledPropFilter.filteringField][
                                    controlledPropFilter.filteringChildField
                                  ],
                            }),
                        }}
                        excludeIds={excludeIds}
                        disabled={isReadOnly && !!selectedValue}
                        selectedValue={selectedValue}
                        sxProps={{ ...(!!fullWidth && { width: "100% !important" }) }}
                      />
                    ) : fieldType === "select" ? (
                      <CustomDropdown
                        variant="outlined"
                        label={labelKey}
                        field={field}
                        defaultValueID={defaultValueID}
                        value={state.item?.[field]}
                        values={values}
                        handleChange={handleChange}
                        required={required}
                      />
                    ) : fieldType === "checkbox" ? (
                      <FormControlLabel
                        aria-required={required}
                        control={
                          <Checkbox
                            checked={state.item?.[field]}
                            onChange={(_, checked: boolean) => handleChange(field, checked)}
                          />
                        }
                        label={labelKey}
                        labelPlacement="end"
                      />
                    ) : fieldType === "disablingCheckbox" ? (
                      <FormControlLabel
                        aria-required={required}
                        control={
                          <Checkbox
                            checked={
                              !!dependentField
                                ? disablingFields.fields.filter((_disablingField) => _disablingField.field === field)[0]
                                    .value
                                : false
                            }
                            onChange={(_, checked: boolean) => handleDisablingfieldChange(field, checked)}
                          />
                        }
                        label={labelKey}
                        labelPlacement="end"
                      />
                    ) : fieldType === "period" ? (
                      <CustomDatePicker
                        handlePeriodChange={(value) => handleChange(field, value)}
                        period={state.item?.[field] ?? ""}
                      />
                    ) : fieldType === "dateTimePicker" ? (
                      <DateTimePicker
                        label={labelKey}
                        value={state.item?.[field]}
                        // inputFormat="yyyy/MM/dd HH:mm"
                        onChange={(value) => {
                          handleChange(field, value);
                        }}
                        renderInput={(params) => <TextField {...params} />}
                        ampm={false}
                      />
                    ) : fieldType === "datePicker" ? (
                      <DesktopDatePicker
                        label={labelKey}
                        value={state.item?.[field]}
                        onChange={(newValue) => {
                          handleChange(field, newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required={required}
                          />
                        )}
                        disabled={
                          !!disablingField
                            ? disablingFields.fields.filter(
                                (_disablingField) => _disablingField.field === disablingField
                              )[0].value
                            : false
                        }
                      />
                    ) : fieldType === "radio" ? (
                      <FormControl>
                        <FormLabel id={field}>{labelForRadio}</FormLabel>
                        <RadioGroup
                          key={field}
                          name={field}
                          defaultValue={defaultValue ?? state.item?.[field]}
                          onChange={(event, value) => {
                            handleChange(field, value === "true" ? true : value === "false" ? false : value);
                          }}
                        >
                          {values?.map((value, idx): JSX.Element => {
                            return (
                              <FormControlLabel
                                key={idx}
                                value={value}
                                control={<Radio />}
                                label={labelKey.split("||")[idx]}
                              />
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                    ) : (
                      <></>
                    )}
                  </React.Fragment>
                );
              }
            )}
        </Content>
        <DialogActions
          handleCancel={handleCloseDialog}
          handleSave={handleSave}
          disabledSaveButton={
            formFields &&
            (effect === "add"
              ? validateOnAddItem(formFields, state.item) || !!fieldsWithTypeEmailNotValidated.length
              : validateOnEditItem(formFields, state.item, state.resetItem) || !!fieldsWithTypeEmailNotValidated.length)
          }
          error={state.error}
          loading={state.loading}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.isConfirmationChangesDialogOpen}
        handleCancel={() => setIsConfirmationChangesDialogOpen(false)}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AddEditItem;
